import React, { useEffect, useState, useCallback } from 'react';
import {
  Lock, Crown, Bot, Briefcase, Calendar, Bell, RefreshCw, Plus,
  ChevronRight, TrendingUp, TrendingDown, Trash2, BellRing, BellOff,
  ArrowUpRight, ArrowDownRight, Clock, AlertCircle, Sparkles
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getSubToken = () => localStorage.getItem('subscriber_token');
const authHeader = () => {
  const t = getSubToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// ============================================================
// PremiumGate — universal wrapper that blurs content for non-subscribers
// ============================================================
export const PremiumGate = ({ access, title, subtitle, children, previewSlot, testId }) => {
  if (access?.has_active_subscription) return children;
  return (
    <div className="relative" data-testid={testId}>
      <div className="filter blur-md pointer-events-none select-none opacity-60">
        {previewSlot || children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center p-4 bg-gradient-to-b from-transparent via-black/70 to-black/90 rounded-2xl">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-golden to-amber-600 flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-black" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">{title || 'Premium Feature'}</h3>
          <p className="text-gray-300 mb-5 text-sm md:text-base">
            {subtitle || 'Unlock with any active Upper Circuit subscription — Options Only, Complete Package, or Equity Only.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            {!access?.logged_in && (
              <Link to="/login" className="bg-zinc-800 hover:bg-zinc-700 border border-golden/40 text-golden font-semibold px-5 py-2.5 rounded-lg flex items-center justify-center gap-2" data-testid="premium-login-cta">
                Login First
              </Link>
            )}
            <Link to="/welcome#services" className="bg-golden hover:bg-golden/90 text-black font-bold px-5 py-2.5 rounded-lg flex items-center justify-center gap-2" data-testid="premium-subscribe-cta">
              <Crown className="w-4 h-4" /> Subscribe Now <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// AI Trade Idea
// ============================================================
export const TradeIdeaTab = ({ access }) => {
  const [idea, setIdea] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchIdea = useCallback(async (force = false) => {
    setLoading(true);
    try {
      if (access?.has_active_subscription) {
        const r = await fetch(`${API_URL}/api/market-intelligence/trade-idea${force ? '?force=true' : ''}`, { headers: authHeader() });
        const j = await r.json();
        setIdea(j);
      } else {
        const r = await fetch(`${API_URL}/api/market-intelligence/trade-idea/preview`);
        setPreview(await r.json());
      }
    } finally {
      setLoading(false);
    }
  }, [access?.has_active_subscription]);

  useEffect(() => { fetchIdea(false); }, [fetchIdea]);

  if (!access?.has_active_subscription) {
    return (
      <PremiumGate
        access={access}
        title="🤖 AI Trade Idea of the Day"
        subtitle="One high-conviction, Claude-AI-generated actionable trade setup with entry, targets, stop-loss & position sizing — refreshed daily."
        testId="trade-idea-gate"
        previewSlot={
          <div className="rounded-2xl border border-golden/30 bg-gradient-to-br from-zinc-900 to-black p-8" data-testid="trade-idea-preview">
            <h2 className="text-2xl font-bold text-white mb-2">{preview?.preview?.heading || 'Premium Trade Idea'}</h2>
            <div className="text-golden mb-4 text-sm" dangerouslySetInnerHTML={{ __html: (preview?.preview?.bias_line || '').replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
            <div className="space-y-3">
              <div className="bg-zinc-800/50 rounded-lg p-3">
                <div className="text-xs text-gray-400 uppercase">Entry Zone</div>
                <div className="text-xl font-bold text-white">XX,XXX – XX,XXX</div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <div className="text-xs text-gray-400 uppercase">Target 1</div>
                  <div className="text-lg font-bold text-emerald-400">XX,XXX</div>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <div className="text-xs text-gray-400 uppercase">Target 2</div>
                  <div className="text-lg font-bold text-emerald-400">XX,XXX</div>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <div className="text-xs text-gray-400 uppercase">Stop Loss</div>
                  <div className="text-lg font-bold text-red-400">XX,XXX</div>
                </div>
              </div>
            </div>
          </div>
        }
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="trade-idea-tab">
      <div className="relative overflow-hidden rounded-2xl border border-golden/30 bg-gradient-to-br from-zinc-950 via-black to-zinc-950 p-6 md:p-8">
        <div className="absolute top-0 right-0 w-72 h-72 bg-golden/10 rounded-full blur-3xl"></div>
        <div className="relative">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-golden to-amber-600 flex items-center justify-center">
                <Bot className="w-6 h-6 text-black" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">AI Trade Idea</h2>
                <p className="text-sm text-gray-400">Claude AI · Refreshes every 6 hours · Premium</p>
              </div>
            </div>
            <button
              onClick={() => fetchIdea(true)}
              disabled={loading}
              className="bg-golden/20 hover:bg-golden/30 border border-golden/40 text-golden font-semibold px-4 py-2 rounded-lg flex items-center gap-2 text-sm disabled:opacity-50"
              data-testid="trade-idea-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Generating…' : 'Re-generate'}
            </button>
          </div>
          {loading && !idea && (
            <div className="space-y-2 animate-pulse">
              <div className="h-4 bg-golden/10 rounded w-3/4"></div>
              <div className="h-3 bg-golden/10 rounded w-5/6"></div>
              <div className="h-3 bg-golden/10 rounded w-2/3"></div>
            </div>
          )}
          {idea?.error && <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-lg p-3">{idea.error}</div>}
          {idea?.idea && <FormattedMarkdown text={idea.idea} testId="trade-idea-content" />}
          {idea?.generated_at && (
            <div className="text-xs text-gray-500 mt-4">
              Generated {new Date(idea.generated_at).toLocaleString('en-IN')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Block / Bulk Deals
// ============================================================
export const BlockDealsTab = ({ access }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!access?.has_active_subscription) return;
    setLoading(true);
    fetch(`${API_URL}/api/market-intelligence/block-deals`, { headers: authHeader() })
      .then(r => r.json()).then(setData).finally(() => setLoading(false));
  }, [access?.has_active_subscription]);

  if (!access?.has_active_subscription) {
    return (
      <PremiumGate
        access={access}
        title="💼 Block & Bulk Deals"
        subtitle="See which mutual funds, FIIs and HNIs are buying or selling — institutional deals over ₹10 Cr from NSE, updated daily."
        testId="block-deals-gate"
        previewSlot={
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2].map((n, i) => (
              <div key={i} className="rounded-xl border border-golden/20 bg-zinc-900/60 p-4">
                <div className="h-5 bg-zinc-800 rounded w-1/2 mb-3"></div>
                {[1, 2, 3, 4].map((_, k) => (
                  <div key={k} className="flex justify-between py-2 border-b border-white/5">
                    <div className="h-3 bg-zinc-800 rounded w-1/3"></div>
                    <div className="h-3 bg-zinc-800 rounded w-1/4"></div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        }
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="block-deals-tab">
      <div className="flex items-center gap-3">
        <Briefcase className="w-6 h-6 text-golden" />
        <div>
          <h2 className="text-2xl font-bold text-white">Block & Bulk Deals</h2>
          <p className="text-sm text-gray-400">Institutional moves · NSE · Updated every 30 min</p>
        </div>
      </div>
      {loading && <div className="text-gray-400 text-sm">Loading…</div>}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <DealsTable title="Block Deals" subtitle="≥ 5,00,000 shares OR ₹10+ Cr" rows={data?.block_deals || []} testId="block-deals-table" />
        <DealsTable title="Bulk Deals" subtitle="≥ 0.5% of equity capital" rows={data?.bulk_deals || []} testId="bulk-deals-table" />
      </div>
    </div>
  );
};

const DealsTable = ({ title, subtitle, rows, testId }) => (
  <div className="rounded-xl border border-golden/20 bg-zinc-900/60 p-4" data-testid={testId}>
    <div className="mb-3">
      <h3 className="text-white font-bold">{title}</h3>
      <p className="text-xs text-gray-400">{subtitle}</p>
    </div>
    {rows.length === 0 ? (
      <div className="text-gray-500 text-sm py-4 text-center">No recent deals</div>
    ) : (
      <div className="space-y-1.5 max-h-[500px] overflow-y-auto pr-1">
        {rows.map((r, i) => {
          const buy = (r.buy_sell || '').toUpperCase().includes('BUY') || (r.buy_sell || '').toUpperCase() === 'B';
          return (
            <div key={i} className="bg-black/40 rounded-md px-3 py-2 text-sm">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-white truncate">{r.symbol || '—'}</div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${buy ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'bg-red-500/20 text-red-300 border border-red-500/40'}`}>
                  {buy ? 'BUY' : 'SELL'}
                </span>
              </div>
              <div className="text-xs text-gray-400 truncate mt-1">{r.client || '—'}</div>
              <div className="flex items-center justify-between mt-1 text-xs">
                <span className="text-gray-300">Qty: {r.quantity || '—'}</span>
                <span className="text-gray-300">@ ₹{r.price || '—'}</span>
                {r.value_cr && <span className="text-golden font-semibold">₹{r.value_cr} Cr</span>}
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

// ============================================================
// Event Calendar
// ============================================================
export const EventCalendarTab = ({ access }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    const endpoint = access?.has_active_subscription
      ? `${API_URL}/api/market-intelligence/events?days=30`
      : `${API_URL}/api/market-intelligence/events?preview=true&days=30`;
    fetch(endpoint, { headers: authHeader() }).then(r => r.json()).then(setData);
  }, [access?.has_active_subscription]);

  const events = data?.events || [];
  const grouped = events.reduce((acc, e) => {
    const d = e.event_date || 'Unknown';
    acc[d] = acc[d] || [];
    acc[d].push(e);
    return acc;
  }, {});

  if (!access?.has_active_subscription) {
    return (
      <PremiumGate
        access={access}
        title="📅 Event Calendar"
        subtitle="RBI policy meetings, FOMC, quarterly results, IPO calendar, budget dates — never miss a market-moving event."
        testId="event-calendar-gate"
        previewSlot={<EventList grouped={grouped} blurred />}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="event-calendar-tab">
      <div className="flex items-center gap-3">
        <Calendar className="w-6 h-6 text-golden" />
        <div>
          <h2 className="text-2xl font-bold text-white">Event Calendar</h2>
          <p className="text-sm text-gray-400">Next 30 days · Premium</p>
        </div>
      </div>
      {events.length === 0 ? (
        <div className="rounded-xl border border-dashed border-golden/20 bg-zinc-900/40 p-6 text-center text-gray-400 text-sm">
          No events scheduled. Admin can add events from the admin panel.
        </div>
      ) : (
        <EventList grouped={grouped} />
      )}
    </div>
  );
};

const importanceColor = (imp) => ({
  high: 'bg-red-500/20 text-red-300 border-red-500/40',
  medium: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
  low: 'bg-gray-500/20 text-gray-300 border-gray-500/40',
}[imp] || 'bg-gray-500/20 text-gray-300 border-gray-500/40');

const categoryEmoji = (cat) => ({
  rbi: '🏦', fomc: '🇺🇸', earnings: '📊', budget: '💰', macro: '🌍', ipo: '🚀'
}[cat] || '📌');

const EventList = ({ grouped, blurred }) => (
  <div className="space-y-4">
    {Object.entries(grouped).map(([date, items]) => (
      <div key={date}>
        <div className="text-golden text-sm font-bold uppercase tracking-wider mb-2">{date}</div>
        <div className="space-y-2">
          {items.map((e, i) => (
            <div key={i} className="rounded-lg border border-golden/15 bg-zinc-900/60 p-4 flex items-start gap-3" data-testid={`event-${e.id || i}`}>
              <span className="text-2xl">{categoryEmoji(e.category)}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className={`font-semibold ${blurred ? 'text-gray-500 select-none' : 'text-white'}`}>{e.title}</h4>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border font-bold uppercase ${importanceColor(e.importance)}`}>
                    {e.importance}
                  </span>
                  {e.event_time && <span className="text-xs text-gray-400 flex items-center gap-1"><Clock className="w-3 h-3" />{e.event_time}</span>}
                </div>
                {e.description && <p className={`text-sm mt-1 ${blurred ? 'text-gray-600' : 'text-gray-300'}`}>{e.description}</p>}
                {e.link && !blurred && (
                  <a href={e.link} target="_blank" rel="noreferrer" className="text-golden text-xs hover:underline mt-1 inline-block">More info →</a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

// ============================================================
// Smart Alerts
// ============================================================
export const SmartAlertsTab = ({ access }) => {
  const [alerts, setAlerts] = useState([]);
  const [metrics, setMetrics] = useState({ metrics: [], operators: [], channels: [] });
  const [form, setForm] = useState({ metric: 'nifty_pcr', operator: 'gt', threshold: '', label: '', channels: ['email'] });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const loadAlerts = useCallback(async () => {
    if (!access?.has_active_subscription) return;
    const r = await fetch(`${API_URL}/api/market-intelligence/alerts`, { headers: authHeader() });
    const j = await r.json();
    setAlerts(j.alerts || []);
  }, [access?.has_active_subscription]);

  useEffect(() => {
    fetch(`${API_URL}/api/market-intelligence/alerts/metrics`).then(r => r.json()).then(setMetrics);
    loadAlerts();
  }, [loadAlerts]);

  const handleCreate = async () => {
    setErr(null);
    if (!form.threshold || isNaN(parseFloat(form.threshold))) {
      setErr('Enter a valid threshold number');
      return;
    }
    setSaving(true);
    try {
      const body = { ...form, threshold: parseFloat(form.threshold) };
      const r = await fetch(`${API_URL}/api/market-intelligence/alerts`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || 'Failed to create alert');
      }
      setForm({ metric: 'nifty_pcr', operator: 'gt', threshold: '', label: '', channels: ['email'] });
      loadAlerts();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleAlert = async (id, enabled) => {
    await fetch(`${API_URL}/api/market-intelligence/alerts/${id}`, {
      method: 'PATCH',
      headers: { ...authHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    });
    loadAlerts();
  };

  const deleteAlert = async (id) => {
    if (!window.confirm('Delete this alert?')) return;
    await fetch(`${API_URL}/api/market-intelligence/alerts/${id}`, {
      method: 'DELETE', headers: authHeader(),
    });
    loadAlerts();
  };

  if (!access?.has_active_subscription) {
    return (
      <PremiumGate
        access={access}
        title="🔔 Smart Alerts"
        subtitle="Set custom thresholds on Nifty PCR, India VIX, FII flow & more. Get email/Telegram pings the moment markets hit your trigger."
        testId="smart-alerts-gate"
        previewSlot={
          <div className="space-y-3">
            <div className="rounded-xl border border-golden/20 bg-zinc-900/60 p-4 h-24"></div>
            <div className="rounded-xl border border-golden/20 bg-zinc-900/60 p-4 h-24"></div>
            <div className="rounded-xl border border-golden/20 bg-zinc-900/60 p-4 h-24"></div>
          </div>
        }
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="smart-alerts-tab">
      <div className="flex items-center gap-3">
        <Bell className="w-6 h-6 text-golden" />
        <div>
          <h2 className="text-2xl font-bold text-white">Smart Alerts</h2>
          <p className="text-sm text-gray-400">Get pinged via email/Telegram when markets hit your levels</p>
        </div>
      </div>

      {/* Create form */}
      <div className="rounded-xl border border-golden/30 bg-gradient-to-br from-zinc-900 to-black p-5">
        <h3 className="text-white font-bold mb-3 flex items-center gap-2"><Plus className="w-4 h-4 text-golden" /> Create New Alert</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <select value={form.metric} onChange={(e) => setForm({ ...form, metric: e.target.value })}
            className="bg-zinc-800 border border-golden/30 rounded-lg px-3 py-2 text-white text-sm" data-testid="alert-metric-select">
            {metrics.metrics.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
          <select value={form.operator} onChange={(e) => setForm({ ...form, operator: e.target.value })}
            className="bg-zinc-800 border border-golden/30 rounded-lg px-3 py-2 text-white text-sm" data-testid="alert-operator-select">
            {metrics.operators.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
          <input type="number" step="0.01" placeholder="Threshold" value={form.threshold}
            onChange={(e) => setForm({ ...form, threshold: e.target.value })}
            className="bg-zinc-800 border border-golden/30 rounded-lg px-3 py-2 text-white text-sm" data-testid="alert-threshold-input" />
          <input type="text" placeholder="Label (optional)" value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            className="bg-zinc-800 border border-golden/30 rounded-lg px-3 py-2 text-white text-sm" data-testid="alert-label-input" />
          <button onClick={handleCreate} disabled={saving}
            className="bg-golden hover:bg-golden/90 text-black font-bold rounded-lg px-4 py-2 text-sm disabled:opacity-50" data-testid="alert-create-btn">
            {saving ? 'Saving…' : 'Add Alert'}
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <span className="text-xs text-gray-400">Notify via:</span>
          {metrics.channels.map(c => (
            <label key={c.key} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
              <input type="checkbox" checked={form.channels.includes(c.key)}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setForm(f => ({
                    ...f,
                    channels: checked ? [...f.channels, c.key] : f.channels.filter(x => x !== c.key)
                  }));
                }} />
              {c.label}
            </label>
          ))}
        </div>
        {err && <div className="text-red-400 text-sm mt-2 flex items-center gap-1"><AlertCircle className="w-4 h-4" /> {err}</div>}
      </div>

      {/* Active alerts */}
      <div>
        <h3 className="text-white font-bold mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-golden" /> Your Active Alerts ({alerts.length})</h3>
        {alerts.length === 0 ? (
          <div className="rounded-xl border border-dashed border-golden/20 bg-zinc-900/40 p-6 text-center text-gray-400 text-sm" data-testid="alerts-empty">
            No alerts yet. Create one above — we'll ping you when conditions trigger.
          </div>
        ) : (
          <div className="space-y-2">
            {alerts.map(a => {
              const metricLabel = metrics.metrics.find(m => m.key === a.metric)?.label || a.metric;
              const opLabel = metrics.operators.find(o => o.key === a.operator)?.label || a.operator;
              return (
                <div key={a.id} className="rounded-xl border border-golden/15 bg-zinc-900/60 p-4 flex items-center justify-between gap-3" data-testid={`alert-row-${a.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {a.enabled
                        ? <BellRing className="w-4 h-4 text-emerald-400" />
                        : <BellOff className="w-4 h-4 text-gray-500" />}
                      <span className="text-white font-semibold">{a.label || `${metricLabel} alert`}</span>
                    </div>
                    <div className="text-sm text-gray-400 mt-1">
                      {metricLabel} {opLabel} <span className="font-semibold text-white">{a.threshold}</span>
                      {' · '}
                      <span className="text-xs">Channels: {(a.channels || []).join(', ')}</span>
                      {a.last_triggered_at && <span className="text-xs ml-2 text-golden">· Last fired: {new Date(a.last_triggered_at).toLocaleString('en-IN')}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => toggleAlert(a.id, !a.enabled)} className="text-xs bg-zinc-800 hover:bg-zinc-700 border border-golden/30 px-3 py-1.5 rounded-lg text-golden" data-testid={`alert-toggle-${a.id}`}>
                      {a.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button onClick={() => deleteAlert(a.id)} className="text-xs text-red-400 hover:bg-red-500/10 p-1.5 rounded-lg" data-testid={`alert-delete-${a.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// Lightweight markdown for Claude output (reused from main file)
// ============================================================
const FormattedMarkdown = ({ text, testId }) => {
  const lines = (text || '').split('\n').filter(l => l.trim() !== '');
  return (
    <div className="space-y-2" data-testid={testId}>
      {lines.map((line, i) => {
        const isBullet = /^\s*[-*]\s+/.test(line);
        const isH2 = /^##\s+/.test(line);
        const isH3 = /^###\s+/.test(line);
        const cleaned = line.replace(/^\s*[-*]\s+/, '').replace(/^###?\s+/, '');
        const html = cleaned
          .replace(/\*\*(.+?)\*\*/g, '<strong class="text-golden">$1</strong>')
          .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em class="text-gray-400">$1</em>');
        if (isH2) return <h2 key={i} className="text-2xl font-bold text-white mt-3 mb-2" dangerouslySetInnerHTML={{ __html: html }} />;
        if (isH3) return <h3 key={i} className="text-lg font-semibold text-white mt-2 mb-1" dangerouslySetInnerHTML={{ __html: html }} />;
        return isBullet ? (
          <div key={i} className="flex gap-2 text-sm md:text-base">
            <span className="text-golden flex-shrink-0">›</span>
            <span dangerouslySetInnerHTML={{ __html: html }} />
          </div>
        ) : (
          <p key={i} className="text-sm md:text-base text-gray-200" dangerouslySetInnerHTML={{ __html: html }} />
        );
      })}
    </div>
  );
};
